import { useState, useEffect, useRef } from 'react'
import WalletHistoryPanel from './WalletHistoryPanel'
import BackToTop from './BackToTop'
import { useIsPhone } from './hooks/useIsPhone'

// Must match Workspace.jsx's PORTRAIT_MAX — same breakpoint where the
// workspace rotates and this panel becomes a wide horizontal strip.
const PORTRAIT_MAX = 1081

// Below this PANEL width the row layout (calendar left / stats right) stops
// working — the calendar takes its 460px and the stats get squeezed to a
// column of clipped numbers. Stack them instead (stats under the calendar).
// Measured on the PANEL, not the window: this panel is a workspace cell that
// can be resized, split, or docked at any width the window never reports.
const STACK_BELOW_PX = 750

/**
 * JournalPanel — wallet (Solana) / futures (Hyperliquid) trade history.
 *
 * Renders WalletHistoryPanel for the active data source. The bot-journal
 * analysis view stays in the chart for now, so this panel has no Wallet/Bot
 * toggle — WalletHistoryPanel's internal toolbar is hidden (hideToolbar) and
 * this panel supplies its own header with a refresh control instead.
 *
 * Props:
 *   dataSource, publicKey, hlWallet
 *   tradeHistory   useTradeHistory() return  { history, loading, refresh }
 *   hlFills        useHlFills() return        { fills, loading, refresh }
 *   formatCompact, onSelectMint, showToast
 */
export default function JournalPanel({
  dataSource,
  publicKey,
  hlWallet,
  tradeHistory,
  hlFills,
  formatCompact,
  onSelectMint,
  showToast,
}) {
  // Anchor for BackToTop. .journal-panel is overflow:hidden and never scrolls
  // itself — the scroller lives inside WalletHistoryPanel and MOVES with the
  // breakpoint (outer div when isLandscape, inner history column when not).
  // BackToTop finds it by capture, so this ref only has to be a stable,
  // non-scrolling box to hang the button off.
  const rootRef = useRef(null)

  const isFutures = dataSource === 'hyperliquid'
  const loading = isFutures ? hlFills.loading : tradeHistory.loading

  // Orientation drives WalletHistoryPanel's internal layout. NOTE the
  // prop's semantics are inverted from its name: passing true STACKS
  // content under a full-width calendar (right for the narrow landscape
  // cell); false is the row layout — calendar fixed-width LEFT, history
  // filling the right — which is what the wide portrait strip wants.
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' ? window.innerWidth < PORTRAIT_MAX : false
  )
  // A TRUE phone is not the same animal as tablet-portrait: the row layout
  // (calendar fixed-width LEFT) assumed a wide full-width strip cell, but on a
  // ~380px phone the calendar eats the entire width and the history sits
  // invisible off the right edge -- "only the calendar shows up". On real
  // phones force the STACKED mode (isLandscape=true per the inverted prop
  // semantics above); a narrow-but-not-phone window keeps the row layout it
  // was built for.
  //
  // Device, not width (hooks/useIsPhone), and therefore not state: this was a
  // live (max-width: 768px) subscription, so a phone in landscape (~852px)
  // dropped back to the row layout and hid the history off-screen — the exact
  // failure the comment above describes, re-entered by rotating.
  const isPhone = useIsPhone()

  // Panel-width watch (ResizeObserver on the panel's own box).
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0
      if (w > 0) setNarrow(w < STACK_BELOW_PX)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(`(max-width: ${PORTRAIT_MAX - 1}px)`)
    const apply = () => setIsPortrait(mq.matches)
    apply()
    if (mq.addEventListener) mq.addEventListener('change', apply)
    else mq.addListener(apply)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply)
      else mq.removeListener(apply)
    }
  }, [])

  const handleRefresh = () => {
    if (isFutures) {
      hlFills.refresh()
      showToast?.('Refreshing futures history…', 'buy')
    } else {
      try { localStorage.removeItem(`solcheddar_tradehistory_${publicKey?.toString().slice(0, 8)}`) } catch {}
      tradeHistory.refresh(true)
      showToast?.('Refreshing wallet history…', 'buy')
    }
  }

  return (
    <div className="journal-panel" ref={rootRef} style={{ position: 'relative' }}>
      <style>{`
        /* Carry the example's 45° cut through every element INSIDE the panel.
           corner-shape: bevel converts each element's existing border-radius
           into a chamfer — the stat cards, calendar cells, token rows and the
           refresh button all pick it up at their own radius, with no color
           changes and no edits to their inline styles. The win-rate ring is an
           SVG, so it's unaffected. No outer border. */
        .journal-panel * { corner-shape: bevel; }
      `}</style>
      <div className="journal-panel-header">
        <span className="journal-panel-title">Journal</span>
        <button
          className={`journal-refresh-btn${loading ? ' spinning' : ''}`}
          onClick={handleRefresh}
          title="Clear cache & re-pull history"
          aria-label="Refresh history"
        >
          {/* circular refresh arrow */}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
        </button>
      </div>
      {isFutures ? (
        <WalletHistoryPanel
          hideToolbar
          mode="futures"
          fills={hlFills.fills}
          hlConnected={!!hlWallet?.accountAddress}
          loading={hlFills.loading}
          onRefresh={handleRefresh}
          onSelectMint={() => {}}
          formatCompact={formatCompact}
          isLandscape={(isPhone || narrow) ? true : !isPortrait}
        />
      ) : (
        <WalletHistoryPanel
          hideToolbar
          history={tradeHistory.history}
          loading={tradeHistory.loading}
          publicKey={publicKey}
          onRefresh={handleRefresh}
          onSelectMint={(mint) => onSelectMint?.(mint)}
          formatCompact={formatCompact}
          isLandscape={(isPhone || narrow) ? true : !isPortrait}
        />
      )}
      <BackToTop containerRef={rootRef} />
    </div>
  )
}